import React, {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

import {termsSections, Section} from './termsSections';
import {ReactComponent as PageBackIcon} from '../assets/icons/page-back.svg?react';
import {ReactComponent as LogoIcon} from '../assets/logos/cashia_logo_pink.svg?react';
import DefinitionsSection from './TermsAndConditions/components/DefinitionsSection';
import StandardSection from './TermsAndConditions/components/StandardSection';
import TableSection from './TermsAndConditions/components/TableSection';

const TermsAndConditions: React.FC = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>(
    'merchant-general-terms-and-conditions'
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sections: Section[] = termsSections;

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({behavior: 'smooth', block: 'start'});
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = contentRef.current?.scrollTop || 0;
      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: sectionRefs.current[section.id],
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const sectionElement = sectionElements[i];
        if (sectionElement && sectionElement.element) {
          const offsetTop = sectionElement.element.offsetTop - 100;
          if (scrollPosition >= offsetTop) {
            setActiveSection(sectionElement.id);
            break;
          }
        }
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [sections]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-subtleGrey px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between lg:justify-start lg:gap-36">
          <LogoIcon className="h-8" />
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-headlineGrayscale hover:text-pink transition-colors">
            <PageBackIcon className="h-7 w-7" />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="flex max-w-7xl mx-auto">
        {/* Sidebar - Document Outline */}
        <aside className="hidden lg:block w-64 bg-surfaceGrey border-r border-subtleGrey h-[calc(100vh-80px)] sticky top-[80px] overflow-y-auto">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-headlineGrayscale mb-4">
              Document Outline
            </h2>
            <nav className="space-y-2">
              {sections.map((section, index) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors relative ${
                    activeSection === section.id
                      ? 'text-pink font-semibold'
                      : 'text-headlineGrayscale hover:text-pink'
                  }`}>
                  {activeSection === section.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-pink rounded-r" />
                  )}
                  <span className="font-medium">{index + 1}.</span>{' '}
                  {section.title.replace(':', '')}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main
          ref={contentRef}
          className="flex-1 overflow-y-auto h-[calc(100vh-80px)] px-6 lg:px-12 py-8">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, index) => (
              <div
                key={section.id}
                ref={(el) => {
                  sectionRefs.current[section.id] = el;
                }}
                id={section.id}
                className="mb-12 scroll-mt-24">
                {index === 0 ? (
                  <h1 className="text-3xl font-bold text-headlineGrayscale mb-4">
                    {section.title}
                  </h1>
                ) : (
                  <h2 className="text-2xl font-semibold text-headlineGrayscale mb-4">
                    {section.title}
                  </h2>
                )}
                {section.id === 'definitions' ? (
                  <DefinitionsSection content={section.content} />
                ) : section.id === 'provision-of-services:' ? (
                  <TableSection content={section.content} />
                ) : (
                  <StandardSection content={section.content} />
                )}
              </div>
            ))}

            <div className="mt-12 pt-8 border-t border-subtleGrey text-center text-captionGrey">
              <p className="mb-2">
                Thank you for choosing Kashia Services Limited
              </p>
              <p className="text-sm">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TermsAndConditions;
